import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

/**
 * Distinctive route page header.
 *
 * Each deep-dive page passes:
 *  - kicker, title, intro (text)
 *  - accent (color used in pill, glow, divider)
 *  - badge (short letter mark e.g. "P" / "T" / "AI")
 *  - stats (array of {k,v}) — page-specific "what's inside" strip
 *  - primaryCta (label only — clicking opens contact dialog if onOpenContact passed)
 *  - secondaryCta (label + href)
 *  - meta (title, description) for SEO
 */
export function RoutePage({
  kicker,
  title,
  intro,
  accent = "#38BDF8",
  badge,
  stats,
  primaryCta,
  secondaryCta = { label: "View the science", href: "/research" },
  children,
  prev,
  next,
  onOpenContact,
  meta,
}) {
  useReveal();
  useDocumentMeta(meta);

  return (
    <main className="pt-28 md:pt-32 pb-24">
      <header className="max-w-[1180px] mx-auto px-6 lg:px-12 relative">
        {/* Accent glow */}
        <div
          className="pointer-events-none absolute -top-10 -left-20 h-[420px] w-[420px] rounded-full opacity-50 nb-drift"
          aria-hidden
          style={{
            background: `radial-gradient(circle, ${accent}33, transparent 65%)`,
          }}
        />

        <Link
          to="/"
          className="reveal relative inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.22em] text-[#94A3B8] hover:text-white"
        >
          <ArrowLeft size={12} /> Home
        </Link>

        <div className="reveal mt-6 flex flex-wrap items-center gap-3 relative">
          {badge && (
            <span
              className="inline-flex items-center justify-center h-7 w-7 rounded-full font-mono text-[12px] font-semibold"
              style={{
                background: `${accent}22`,
                border: `1px solid ${accent}66`,
                color: accent,
              }}
              aria-hidden
            >
              {badge}
            </span>
          )}
          <span
            className="pill"
            style={{ borderColor: `${accent}44` }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full nb-pulse"
              style={{ background: accent }}
            />
            {kicker}
          </span>
        </div>

        <h1
          className="reveal relative mt-5 font-display text-white text-[clamp(34px,5.2vw,64px)] leading-[1.05] font-medium tracking-tight max-w-3xl"
          style={{
            textShadow: `0 6px 60px ${accent}33, 0 6px 100px ${accent}22`,
          }}
        >
          {title}
        </h1>

        {intro && (
          <p className="reveal relative mt-5 text-[#CBD5E1] text-[17px] leading-relaxed max-w-2xl">
            {intro}
          </p>
        )}

        <div className="reveal relative mt-9 flex flex-wrap gap-3">
          {onOpenContact && primaryCta && (
            <button
              onClick={onOpenContact}
              data-testid="route-primary-cta"
              className="btn-glow inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#020617] text-[13px] font-semibold hover:bg-[#BFDBFE]"
            >
              {primaryCta}
              <ArrowRight size={13} />
            </button>
          )}
          {secondaryCta && (
            <Link
              to={secondaryCta.href}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white text-[13px] hover:border-white/40"
              style={{ borderColor: `${accent}33` }}
            >
              {secondaryCta.label} <ArrowRight size={13} />
            </Link>
          )}
        </div>

        {/* Per-page "what's inside" stats strip */}
        {stats && stats.length > 0 && (
          <div className="reveal relative mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden">
            {stats.map((s, i) => (
              <div key={i} className="bg-[#07111F] px-5 py-4">
                <div
                  className="font-display text-[22px] font-medium"
                  style={{ color: accent }}
                >
                  {s.k}
                </div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.25em] uppercase text-[#94A3B8]">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        )}
      </header>

      <div className="mt-12">{children}</div>

      {/* Prev / Next nav */}
      <nav className="max-w-[1180px] mx-auto px-6 lg:px-12 mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
        {prev ? (
          <Link
            to={prev.href}
            className="reveal group glass rounded-2xl p-6 hover:border-[#38BDF8]/30 transition-colors"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#475569]">
              ← Previous
            </div>
            <div className="mt-2 font-display text-white text-[20px] font-medium group-hover:text-[#BFDBFE]">
              {prev.label}
            </div>
          </Link>
        ) : (
          <div />
        )}
        {next ? (
          <Link
            to={next.href}
            className="reveal group glass rounded-2xl p-6 md:text-right hover:border-[#38BDF8]/30 transition-colors"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#475569]">
              Next →
            </div>
            <div className="mt-2 font-display text-white text-[20px] font-medium group-hover:text-[#BFDBFE]">
              {next.label}
            </div>
          </Link>
        ) : (
          <div />
        )}
      </nav>
    </main>
  );
}
